/**
 * UX Sprint 1 — Project Assistant drawer (slide-over from the right).
 *
 * Wraps the existing CEO chat experience under a friendlier "Project
 * Assistant" label, with 5 quick-action chips that forward to the same
 * `POST /api/projects/{id}/chat` endpoint via `onAskAssistant(prompt)`.
 *
 * Behavior preserved:
 *   - Chat backend untouched. Quick actions just inject prompt text
 *     into the existing chat flow.
 *   - "Open full chat" routes to the legacy `ceo` tab so the full
 *     CeoChatPanel renders in the workspace (and consumes pendingPrompt
 *     identically to the old CeoEmbeddedPanel suggestion chips).
 */
import { useEffect } from "react";
import {
    X, Sparkles, ArrowRight, Wrench, FileText,
    ListChecks, FileDown, SlidersHorizontal,
} from "lucide-react";

const QUICK_ACTIONS = [
    {
        key: "fix",
        label: "Fix current stage",
        icon: Wrench,
        prompt: "The current stage looks stuck or failed. Walk me through exactly what to do next — repair, retry, or manual override.",
    },
    {
        key: "summarize",
        label: "Summarize project",
        icon: FileText,
        prompt: "Give me a concise executive summary of this project — brief, audience, current stage, what's done, what's next.",
    },
    {
        key: "missing",
        label: "Show missing items",
        icon: ListChecks,
        prompt: "Audit this project for missing inputs (client brand assets, competitor seeds, geography, pricing). Tell me what to upload before the next agent runs.",
    },
    {
        key: "client_pdf",
        label: "Prepare client PDF",
        icon: FileDown,
        prompt: "Help me prepare a client-ready PDF. Which export should I download right now, and what should I double-check before sharing?",
    },
];

export default function ProjectAssistantDrawer({
    open, onClose, project, onAskAssistant, onOpenMoreTools,
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const currentStage = (project?.current_stage || "").replaceAll("_", " ");

    return (
        <>
            <div
                data-testid="assistant-backdrop"
                onClick={onClose}
                className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity ${
                    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
                aria-hidden={!open}
            />
            <aside
                data-testid="assistant-drawer"
                aria-hidden={!open}
                className={`fixed top-0 right-0 z-50 h-screen w-full sm:w-[440px] border-l border-zinc-900 bg-gradient-to-br from-[#0e0a08] via-[#0a0a0a] to-black shadow-2xl flex flex-col transition-transform duration-200 ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <header className="px-5 py-4 border-b border-zinc-900 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="shrink-0 w-10 h-10 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 grid place-items-center">
                            <Sparkles className="w-5 h-5 text-[#FF4400]" />
                        </div>
                        <div className="min-w-0">
                            <div className="label-micro text-[#FF4400]">Project Assistant</div>
                            <h2 className="font-display text-lg tracking-tight truncate">
                                {project?.title || "Your project"}
                            </h2>
                            {currentStage && (
                                <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                                    Current stage · {currentStage}
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid="assistant-close"
                        onClick={onClose}
                        className="shrink-0 w-9 h-9 grid place-items-center rounded-sm border border-zinc-900 hover:border-zinc-700 hover:bg-zinc-900/50 text-zinc-400 hover:text-white transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-5 space-y-5">
                    <section>
                        <div className="label-micro mb-2">Quick actions</div>
                        <div className="grid grid-cols-1 gap-2">
                            {QUICK_ACTIONS.map(({ key, label, icon: Icon, prompt }) => (
                                <button
                                    key={key}
                                    type="button"
                                    data-testid={`assistant-quick-${key}`}
                                    onClick={() => { onAskAssistant && onAskAssistant(prompt); onClose && onClose(); }}
                                    className="flex items-center gap-3 px-3 py-3 rounded-sm border border-zinc-800 hover:border-[#FF4400] hover:bg-zinc-900/40 text-left transition-colors group"
                                >
                                    <div className="shrink-0 w-9 h-9 rounded-sm border border-zinc-800 grid place-items-center text-zinc-400 group-hover:text-[#FF4400] group-hover:border-[#FF4400]/40 transition-colors">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white">{label}</div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FF4400] transition-colors" />
                                </button>
                            ))}
                        </div>
                    </section>

                    <section>
                        <div className="label-micro mb-2">Advanced</div>
                        <button
                            type="button"
                            data-testid="assistant-open-advanced"
                            onClick={() => { onOpenMoreTools && onOpenMoreTools(); onClose && onClose(); }}
                            className="w-full flex items-center gap-3 px-3 py-3 rounded-sm border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/40 text-left transition-colors group"
                        >
                            <div className="shrink-0 w-9 h-9 rounded-sm border border-zinc-800 grid place-items-center text-zinc-400 group-hover:text-[#FF4400] group-hover:border-[#FF4400]/40 transition-colors">
                                <SlidersHorizontal className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-white">Open advanced tools</div>
                                <div className="text-[11px] text-zinc-500 mt-0.5">Activity log, QC, prompt library, share link…</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FF4400] transition-colors" />
                        </button>
                    </section>

                    <section>
                        <button
                            type="button"
                            data-testid="assistant-open-full-chat"
                            onClick={() => { onAskAssistant && onAskAssistant(null); onClose && onClose(); }}
                            className="w-full text-center text-xs font-mono uppercase tracking-wider px-3 py-3 rounded-sm border border-zinc-800 hover:border-[#FF4400] hover:text-[#FF4400] transition-colors"
                        >
                            Open full assistant chat →
                        </button>
                    </section>
                </div>

                <footer className="px-5 py-3 border-t border-zinc-900 text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                    Project Assistant uses the same assistant engine — your conversation history is preserved.
                </footer>
            </aside>
        </>
    );
}
